import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { handleGoogleCallback, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Get session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (sessionId) {
        try {
          const result = await handleGoogleCallback(sessionId);
          
          // Check if pending approval
          if (result?.pending) {
            navigate('/login', { replace: true, state: { pendingApproval: true } });
            return;
          }
          
          // Clear the hash
          window.history.replaceState(null, '', window.location.pathname);
          
          // Small delay to ensure state is updated before navigation
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Re-check auth to ensure state is loaded
          await checkAuth();
          
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true, state: { error: 'Authentication failed' } });
        }
      } else {
        // No session_id, redirect to login
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [handleGoogleCallback, checkAuth, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#264653] via-[#1a4a4f] to-[#1a3a42] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#e9c46a] mx-auto mb-4" />
        <p className="text-[#faf3e0] text-lg font-mono">Completing sign in...</p>
      </div>
    </div>
  );
}
